import axios from 'axios';

// Em producao usa subdominio api.cotou.com.br, em dev local usa localhost:8000
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.cotou.com.br';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 120000, // 2 minutos
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token && !config.url?.startsWith('/auth/google')) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Interceptor para tratamento de erros
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('Erro na API:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

export const cotacaoAPI = {
  // Login com Google
  loginGoogle: async (firebaseToken) => {
    const response = await api.post('/auth/google', {
      token: firebaseToken
    });
    return response.data;
  },

  // ==================
  // Admin - Emails
  // ==================

  // Listar emails autorizados
  listarEmailsAutorizados: async () => {
    const response = await api.get('/admin/emails');
    return response.data;
  },

  // Adicionar email autorizado
  adicionarEmailAutorizado: async (email, nome = null, isAdmin = false) => {
    const response = await api.post('/admin/emails', {
      email: email,
      nome: nome,
      is_admin: isAdmin
    });
    return response.data;
  },

  // Atualizar email autorizado
  atualizarEmailAutorizado: async (emailId, dados) => {
    const response = await api.put(`/admin/emails/${emailId}`, dados);
    return response.data;
  },

  // Remover email autorizado
  removerEmailAutorizado: async (emailId) => {
    const response = await api.delete(`/admin/emails/${emailId}`);
    return response.data;
  },

  // Listar todas as cotações
  listarCotacoes: async () => {
    const response = await api.get('/cotacoes/lista');
    return response.data;
  },

  // Obter dados de uma cotação específica
  obterCotacao: async (cotacaoId) => {
    const response = await api.get(`/cotacoes/${cotacaoId}`);
    return response.data;
  },

  // Gerar relatório
  gerarRelatorio: async (cotacaoId, fornecedorComparacao = null) => {
    const response = await api.post('/relatorio/gerar', {
      cotacao_id: cotacaoId,
      fornecedor_comparacao: fornecedorComparacao
    });
    return response.data;
  },

  // Preview do relatório
  previewRelatorio: async (cotacaoId, fornecedorComparacao = null) => {
    const params = fornecedorComparacao ? `?fornecedor_comparacao=${fornecedorComparacao}` : '';
    const response = await api.get(`/relatorio/preview/${cotacaoId}${params}`);
    return response.data;
  },

  // Listar fornecedores disponíveis
  listarFornecedores: async (cotacaoId) => {
    const response = await api.get(`/cotacoes/${cotacaoId}/fornecedores`);
    return response.data;
  },

  // Download do relatório
  downloadRelatorio: (filename) => {
    // Em produção com Docker, usar URL relativa
    const baseUrl = API_BASE_URL.startsWith('/') ? '' : API_BASE_URL;
    return `${baseUrl}/relatorio/download/${filename}`;
  },

  // Limpar cache de cotação específica
  limparCache: async (cotacaoId) => {
    const response = await api.delete(`/cache/${cotacaoId}`);
    return response.data;
  },

  // Obter detalhes de produto específico
  obterDetalhesProduto: async (cotacaoId, produtoId) => {
    const response = await api.get(`/cotacoes/${cotacaoId}/produtos/${produtoId}`);
    return response.data;
  },

  // Enviar relatório por email
  enviarRelatorioEmail: async (cotacaoId, emailDestinatario, fornecedorComparacao = null, assunto = null, mensagem = null) => {
    const response = await api.post('/relatorio/enviar-email', {
      cotacao_id: cotacaoId,
      fornecedor_comparacao: fornecedorComparacao,
      email_destinatario: emailDestinatario,
      assunto: assunto,
      mensagem: mensagem
    });
    return response.data;
  },

  // Verificar configuração de email
  verificarConfiguracaoEmail: async () => {
    const response = await api.get('/config/email/status');
    return response.data;
  },

  // Gerar PDF no backend
  gerarPDFBackend: async (cotacaoId, fornecedorComparacao = null) => {
    const response = await api.post('/relatorio/gerar-pdf', {
      cotacao_id: cotacaoId,
      fornecedor_comparacao: fornecedorComparacao
    }, {
      responseType: 'blob' // Importante para receber arquivo binário
    });
    return response;
  },

  // Gerar PDF com tabela de segundo menor preço
  gerarPDFComSegundoMenor: async (cotacaoId, fornecedorComparacao) => {
    const response = await api.post('/relatorio/gerar-pdf-segundo-menor', {
      cotacao_id: cotacaoId,
      fornecedor_comparacao: fornecedorComparacao
    }, {
      responseType: 'blob' // Importante para receber arquivo binário
    });
    return response;
  }
};

export default api;
